import { GET_EVM_NFTS_BY_OWNERS } from '@repo/api-core-toolkit';
import {
  EVMNft,
  EvmNFTChain,
  EvmNFTCollectionContractName,
} from '@repo/types-client';

import { getEVMNftsFromChain } from './get-evm-nfts-from-chain';

/**
 * Base interface for EVM NFT fetching operations
 * @interface GetEvmNftsInput
 */
interface GetEvmNftsInput {
  /** Source of NFT data - either from indexer or directly from blockchain */
  dataSource: 'indexer' | 'onchain';
  /** The blockchain network to query */
  chain: EvmNFTChain;
  /** Single address for specific NFT queries - undefined in base interface */
  address?: undefined;
  /** Multiple addresses for batch queries - undefined in base interface */
  addresses?: undefined;
  /** Specific token ID - undefined in base interface */
  tokenId?: undefined;
}

/**
 * Interface for fetching a single EVM NFT
 * @interface GetEvmNftsSingularInput
 * @extends {Omit<GetEvmNftsInput, 'dataSource' | 'tokenId'>}
 */
export interface GetEvmNftsSingularInput
  extends Omit<GetEvmNftsInput, 'dataSource' | 'tokenId'> {
  /** Currently restricted to onchain only until indexer implementation */
  dataSource: 'onchain'; // TODO: Remove this when the indexer implementation is ready.
  /** The specific token ID to fetch */
  tokenId: string;
  /** Array of collection contract names to query */
  collections: [EvmNFTCollectionContractName] | EvmNFTCollectionContractName[];
}

/**
 * Interface for fetching a single ERC1155 token
 * @interface GetEvmNftsSingularERC1155Input
 * @extends {Omit<GetEvmNftsInput, 'dataSource' | 'tokenId' | 'address'>}
 */
export interface GetEvmNftsSingularERC1155Input
  extends Omit<GetEvmNftsInput, 'dataSource' | 'tokenId' | 'address'> {
  /** Currently restricted to onchain only until indexer implementation */
  dataSource: 'onchain'; // TODO: Remove this when the indexer implementation is ready.
  /** The specific token ID to fetch */
  tokenId: string;
  /** The contract address for the ERC1155 token */
  address: string | undefined;
  /** Array of collection contract names to query */
  collections: [EvmNFTCollectionContractName] | EvmNFTCollectionContractName[];
}

/**
 * Interface for fetching multiple NFTs by addresses
 * @interface GetEvmNftsPluralInput
 * @extends {Omit<GetEvmNftsInput, 'addresses'>}
 */
export interface GetEvmNftsPluralInput
  extends Omit<GetEvmNftsInput, 'addresses'> {
  /** Array of addresses to fetch NFTs for */
  addresses: string[];
  /** Array of collection contract names to query */
  collections: EvmNFTCollectionContractName[] | EvmNFTCollectionContractName[];
}

export function getEvmNfts<T extends EVMNft>(
  props: GetEvmNftsSingularInput,
): Promise<T>;

export function getEvmNfts<T extends EVMNft>(
  props: GetEvmNftsSingularERC1155Input,
): Promise<T>;

export function getEvmNfts<T extends EVMNft>(
  props: GetEvmNftsPluralInput,
): Promise<T[]>;

/**
 * Fetch EVM NFTs from the indexer or on-chain based on the data source.
 *
 * This function supports three main use cases:
 * 1. Fetching a single NFT by token ID
 * 2. Fetching a single ERC1155 token
 * 3. Fetching multiple NFTs for multiple addresses
 *
 * @template T - Type extending EVMNft
 * @param {GetEvmNftsSingularInput | GetEvmNftsSingularERC1155Input | GetEvmNftsPluralInput} input - Input parameters
 * @param {string[]} input.collections - The collections to fetch NFTs for
 * @param {'indexer' | 'onchain'} input.dataSource - Source of NFT data
 * @param {EvmNFTChain} input.chain - The blockchain network to query
 * @param {string} [input.address] - Optional address for ERC1155 tokens
 * @param {string[]} [input.addresses] - Optional array of addresses for batch queries
 * @param {string} [input.tokenId] - Optional specific token ID to fetch
 *
 * @returns {Promise<T | T[]>} Returns either a single NFT or an array of NFTs
 * @throws Will throw an error if required parameters are missing
 */
export async function getEvmNfts<T extends EVMNft>(
  input:
    | GetEvmNftsSingularInput
    | GetEvmNftsSingularERC1155Input
    | GetEvmNftsPluralInput,
): Promise<T | T[]> {
  const { address, addresses, collections, dataSource, chain, tokenId } = input;

  // Validate required parameters
  if (!collections?.length || !dataSource || !chain) {
    return [];
  }

  // Handle indexer data source
  if (dataSource === 'indexer') {
    const API_URL =
      process.env.NEXT_PUBLIC_GQL_API_URL ||
      'https://core-api.dev.doodles.app/graphql';

    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        query: GET_EVM_NFTS_BY_OWNERS.loc?.source.body,
        variables: {
          data: {
            addresses,
            collections,
          },
        },
      }),
    });

    const json = await response.json();
    return json.data.getEvmNFTsByOwners as T[];
  }

  // Handle onchain data source
  if (tokenId !== undefined) {
    // Fetch single NFT by token ID
    const nft = await getEVMNftsFromChain({
      collections: collections as [EvmNFTCollectionContractName],
      chain,
      tokenId,
      address,
    });
    return nft as T;
  }

  // Fetch multiple NFTs
  const nfts = await getEVMNftsFromChain({ addresses, collections, chain });
  return nfts as T[];
}
